import React, { memo, useCallback, useEffect, useState } from "react";
import { FiMinus, FiPlus } from "react-icons/fi";
import { Button } from "./Button";
import { Selector } from "./Selector";
import { Guest } from "../api/contracts/reservation";
import { GuestTypesConfig } from "../api/contracts/site-config";
import { Info } from "lucide-react";
import { guestAgeSpanLabel, type GuestAgeTypeKey } from "./guestAgeSpan";

export interface GuestSelectLabels {
  adults: string;
  teenagers: string;
  children: string;
  infants: string;
  child: string;
  infant: string;
  age: string;
  adultsSubtitle: string;
  teenagersSubtitle: string;
  childrenSubtitle: string;
  infantsSubtitle: string;
  fromAge: (age: number) => string;
  ageRange: (minAge: number, maxAge: number) => string;
  ageLabel: (age: number) => string;
  /** "Minst N" — the room's own floor for this guest type, shown as a badge on its row. */
  minGuests: (count: number) => string;
  maxLimitHint: {
    adults: string;
    teenagers: string;
    children: string;
    infants: string;
    total: string;
  };
}

const AGE_OPTION_VALUES = Array.from({ length: 11 }, (_, i) => String(i + 2));

const getAgeValues = (minAge: number, maxAge: number) =>
  Array.from({ length: maxAge - minAge + 1 }, (_, i) => String(minAge + i));

interface GuestCounterProps {
  label: string;
  subtitle: string;
  /** Requirement pill beside the label, e.g. a room that cannot be booked under 3 adults. */
  badge?: string;
  count: number;
  onIncrement: () => void;
  onDecrement: () => void;
  minValue: number;
  maxValue: number;
}

const GuestCounter = memo(
  ({
    label,
    subtitle,
    badge,
    count,
    onIncrement,
    onDecrement,
    minValue,
    maxValue,
    disableIncrement = false,
    disableDecrement = false,
  }: GuestCounterProps & {
    disableIncrement?: boolean;
    disableDecrement?: boolean;
  }) => (
    <div className="flex items-center justify-between">
      <div className="flex flex-col">
        <span className="flex items-center gap-2">
          <span className="text-sm font-extrabold text-ink">{label}</span>
          {badge && (
            <span
              className="rounded-badge bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent"
              data-testid="guest-min-badge"
            >
              {badge}
            </span>
          )}
        </span>
        <span className="text-xs text-ink-secondary">{subtitle}</span>
      </div>
      <div className="flex items-center gap-3">
        <Button
          variant="iconSecondary"
          icon={<FiMinus className="h-4 w-4" />}
          onClick={onDecrement}
          disabled={count <= minValue || disableDecrement}
        />
        <span className="w-4 text-center text-sm font-medium">{count}</span>
        <Button
          variant="iconSecondary"
          icon={<FiPlus className="h-4 w-4" />}
          onClick={onIncrement}
          disabled={count >= maxValue || disableIncrement}
        />
      </div>
    </div>
  ),
);

GuestCounter.displayName = "GuestCounter";

interface ChildGuestFormProps {
  index: number;
  guest: Guest;
  onAgeChange: (age: number) => void;
}

const ChildGuestForm = memo(
  ({
    index,
    guest,
    onAgeChange,
    guestTypesConfig,
    guestType,
    labels,
  }: ChildGuestFormProps & {
    guestTypesConfig?: GuestTypesConfig;
    guestType: "child" | "infant";
    labels: GuestSelectLabels;
  }) => {
    const ageValues = guestTypesConfig
      ? getAgeValues(
          guestTypesConfig[guestType].minAge,
          guestTypesConfig[guestType].maxAge,
        )
      : AGE_OPTION_VALUES;
    const ageOptions = ageValues.map((value) => ({
      value,
      label: labels.ageLabel(Number(value)),
    }));

    return (
      <div className="flex flex-col gap-[22px]">
        <span className="text-sm font-extrabold text-ink">
          {labels[guestType]} {index + 1}
        </span>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-light text-ink">
            {labels.age}
          </span>
          <Selector
            options={ageOptions}
            value={String(guest.age)}
            onChange={(value) => onAgeChange(Number(value))}
          />
        </div>
      </div>
    );
  },
);

ChildGuestForm.displayName = "ChildGuestForm";

/** Height-reveal shared by the age forms, so adding a child or infant unfolds
 *  instead of jumping the sheet. CSS grid-rows 0fr-to-1fr rather than a
 *  motion library: the embeddable widget bundles this file, and framer-motion
 *  here cost it ~35 KB gzip. The age Selector portals its dropdown, so the
 *  overflow clip never cuts it. */
const Unfold: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div
      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
};

interface GuestLimits {
  adult: { min: number; max: number };
  teenager: { min: number; max: number };
  child: { min: number; max: number };
  infant: { min: number; max: number };
}

export interface GuestSelectProps {
  adults: number;
  teenagers: number;
  children: number;
  infants: number;
  childGuests: Guest[];
  infantGuests: Guest[];
  onAdultsChange: (value: number) => void;
  onTeenagersChange: (value: number) => void;
  onChildrenChange: (value: number) => void;
  onInfantsChange: (value: number) => void;
  onChildAgeChange: (childId: number, age: number) => void;
  onInfantAgeChange: (infantId: number, age: number) => void;
  guestLimits: GuestLimits;
  totalGuests: number;
  maxTotalGuests: number;
  guestTypesConfig?: GuestTypesConfig;
  fromMobileSearchSheet?: boolean;
  /**
   * Freezes every counter. Set while availability or price for the current
   * guest mix is in flight: each step re-queries both, so an unfrozen stepper
   * lets the guest stack up changes against numbers already on their way out.
   */
  disabled?: boolean;
  hideMaxLimitHint?: boolean;
  labels: GuestSelectLabels;
}

export const GuestSelect: React.FC<GuestSelectProps> = memo(
  ({
    adults,
    teenagers,
    children,
    infants,
    childGuests,
    infantGuests,
    onAdultsChange,
    onTeenagersChange,
    onChildrenChange,
    onInfantsChange,
    onChildAgeChange,
    onInfantAgeChange,
    guestLimits,
    totalGuests,
    maxTotalGuests,
    guestTypesConfig,
    fromMobileSearchSheet = false,
    disabled = false,
    hideMaxLimitHint = false,
    labels,
  }) => {
    const getGuestSubtitle = (
      guestType: GuestAgeTypeKey,
      guestTypesConfig?: GuestTypesConfig,
    ) => guestAgeSpanLabel(guestType, labels, guestTypesConfig);

    const handleAdultIncrement = useCallback(() => {
      if (adults < guestLimits.adult.max && totalGuests < maxTotalGuests) {
        onAdultsChange(adults + 1);
      }
    }, [
      adults,
      onAdultsChange,
      guestLimits.adult.max,
      totalGuests,
      maxTotalGuests,
    ]);

    // Decrements also stop at one guest in total: per-type minimums come from
    // site config and default to 0, but a search with nobody in it is never
    // wanted, so the last remaining guest of any type stays.
    const handleAdultDecrement = useCallback(() => {
      if (adults > guestLimits.adult.min && totalGuests > 1) {
        onAdultsChange(adults - 1);
      }
    }, [adults, onAdultsChange, guestLimits.adult.min, totalGuests]);

    const handleTeenagerIncrement = useCallback(() => {
      if (
        teenagers < guestLimits.teenager.max &&
        totalGuests < maxTotalGuests
      ) {
        onTeenagersChange(teenagers + 1);
      }
    }, [
      teenagers,
      onTeenagersChange,
      guestLimits.teenager.max,
      totalGuests,
      maxTotalGuests,
    ]);

    const handleTeenagerDecrement = useCallback(() => {
      if (teenagers > guestLimits.teenager.min && totalGuests > 1) {
        onTeenagersChange(teenagers - 1);
      }
    }, [teenagers, onTeenagersChange, guestLimits.teenager.min, totalGuests]);

    const handleChildIncrement = useCallback(() => {
      if (children < guestLimits.child.max && totalGuests < maxTotalGuests) {
        onChildrenChange(children + 1);
      }
    }, [
      children,
      onChildrenChange,
      guestLimits.child.max,
      totalGuests,
      maxTotalGuests,
    ]);

    const handleChildDecrement = useCallback(() => {
      if (children > guestLimits.child.min && totalGuests > 1) {
        onChildrenChange(children - 1);
      }
    }, [children, onChildrenChange, guestLimits.child.min, totalGuests]);

    const handleInfantIncrement = useCallback(() => {
      if (infants < guestLimits.infant.max && totalGuests < maxTotalGuests) {
        onInfantsChange(infants + 1);
      }
    }, [
      infants,
      onInfantsChange,
      guestLimits.infant.max,
      totalGuests,
      maxTotalGuests,
    ]);

    const handleInfantDecrement = useCallback(() => {
      if (infants > guestLimits.infant.min && totalGuests > 1) {
        onInfantsChange(infants - 1);
      }
    }, [infants, onInfantsChange, guestLimits.infant.min, totalGuests]);

    return (
      <div
        className={`w-full min-w-0 rounded-3xl bg-surface-card md:min-w-[336px]`}
      >
        <div className="flex flex-col divide-y divide-border">
          {/* Adults Section - Only show if max > 0 */}
          {guestLimits.adult.max > 0 && (
          <div
            className={fromMobileSearchSheet ? "border-t border-border" : ""}
          >
            <div className="px-4 py-4">
              <GuestCounter
                label={labels.adults}
                subtitle={getGuestSubtitle("adult", guestTypesConfig)}
                badge={guestLimits.adult.min > 1 ? labels.minGuests(guestLimits.adult.min) : undefined}
                count={adults}
                onIncrement={handleAdultIncrement}
                onDecrement={handleAdultDecrement}
                minValue={guestLimits.adult.min}
                maxValue={guestLimits.adult.max}
                disableIncrement={disabled || totalGuests >= maxTotalGuests}
                disableDecrement={disabled || totalGuests <= 1}
              />
            </div>
          </div>
          )}

          {/* Teenagers Section - Only show if max > 0 */}
          {guestLimits.teenager.max > 0 && (
            <div>
              <div className="px-4 py-4">
                <GuestCounter
                  label={labels.teenagers}
                  subtitle={getGuestSubtitle("teenager", guestTypesConfig)}
                  badge={guestLimits.teenager.min > 0 ? labels.minGuests(guestLimits.teenager.min) : undefined}
                  count={teenagers}
                  onIncrement={handleTeenagerIncrement}
                  onDecrement={handleTeenagerDecrement}
                  minValue={guestLimits.teenager.min}
                  maxValue={guestLimits.teenager.max}
                  disableIncrement={disabled || totalGuests >= maxTotalGuests}
                  disableDecrement={disabled || totalGuests <= 1}
                />
              </div>
            </div>
          )}

          {/* Children Section - Only show if max > 0 */}
          {guestLimits.child.max > 0 && (
            <div className="flex flex-col gap-4">
              <div className="px-4 py-4">
                <GuestCounter
                  label={labels.children}
                  subtitle={getGuestSubtitle("child", guestTypesConfig)}
                  badge={guestLimits.child.min > 0 ? labels.minGuests(guestLimits.child.min) : undefined}
                  count={children}
                  onIncrement={handleChildIncrement}
                  onDecrement={handleChildDecrement}
                  minValue={guestLimits.child.min}
                  maxValue={guestLimits.child.max}
                  disableIncrement={disabled || totalGuests >= maxTotalGuests}
                  disableDecrement={disabled || totalGuests <= 1}
                />
              </div>

              {/* Child Guest Forms */}
              {children > 0 && (
                <Unfold>
                  <div className="flex flex-col px-4">
                    {childGuests.map((guest, index) => (
                      <Unfold key={index}>
                        <div className="pb-[18px]">
                          <ChildGuestForm
                            index={index}
                            guest={guest}
                            onAgeChange={(age) => onChildAgeChange(index, age)}
                            guestTypesConfig={guestTypesConfig}
                            guestType="child"
                            labels={labels}
                          />
                        </div>
                      </Unfold>
                    ))}
                  </div>
                </Unfold>
              )}
            </div>
          )}

          {/* Infants Section - Only show if max > 0 */}
          {guestLimits.infant.max > 0 && (
            <div className="flex flex-col gap-4">
              <div className="px-4 py-4">
                <GuestCounter
                  label={labels.infants}
                  subtitle={getGuestSubtitle("infant", guestTypesConfig)}
                  badge={guestLimits.infant.min > 0 ? labels.minGuests(guestLimits.infant.min) : undefined}
                  count={infants}
                  onIncrement={handleInfantIncrement}
                  onDecrement={handleInfantDecrement}
                  minValue={guestLimits.infant.min}
                  maxValue={guestLimits.infant.max}
                  disableIncrement={disabled || totalGuests >= maxTotalGuests}
                  disableDecrement={disabled || totalGuests <= 1}
                />
              </div>

              {/* Infant Guest Forms */}
              {infants > 0 && (
                <Unfold>
                  <div className="flex flex-col px-4">
                    {infantGuests.map((guest, index) => (
                      <Unfold key={index}>
                        <div className="pb-[18px]">
                          <ChildGuestForm
                            index={index}
                            guest={guest}
                            onAgeChange={(age) => onInfantAgeChange(index, age)}
                            guestTypesConfig={guestTypesConfig}
                            guestType="infant"
                            labels={labels}
                          />
                        </div>
                      </Unfold>
                    ))}
                  </div>
                </Unfold>
              )}
            </div>
          )}
        </div>

        {/* Max limit hint */}
        {(() => {
          let hintKey = null;
          if (hideMaxLimitHint) return null;
          if (totalGuests >= maxTotalGuests) {
            hintKey = "total";
          } else if (totalGuests > 0) {
            if (adults === guestLimits.adult.max && guestLimits.adult.max > 0) {
              hintKey = "adults";
            } else if (
              teenagers === guestLimits.teenager.max &&
              guestLimits.teenager.max > 0
            ) {
              hintKey = "teenagers";
            } else if (
              children === guestLimits.child.max &&
              guestLimits.child.max > 0
            ) {
              hintKey = "children";
            } else if (
              infants === guestLimits.infant.max &&
              guestLimits.infant.max > 0
            ) {
              hintKey = "infants";
            }
          }
          return hintKey ? (
            <div
              className="mt-4 flex items-center px-5 pb-4 text-xs font-medium text-accent"
              data-testid="guest-max-hint"
            >
              <Info
                size={16}
                className="mr-2 flex-shrink-0 text-accent"
              />
              {labels.maxLimitHint[hintKey as keyof GuestSelectLabels["maxLimitHint"]]}
            </div>
          ) : null;
        })()}
      </div>
    );
  },
);

GuestSelect.displayName = "GuestSelect";
